'use client';

/**
 * @packageDocumentation
 * @category Hook
 */

import 'BomdisoftComponents/tab/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { overflowEdges, pageTravel, tabOverflowLabels } from 'BomdisoftComponents/tab/delegate';
import type { TabOverflowLabels } from 'BomdisoftComponents/tab/render';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { getLanguageFromLocale } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/* The first edge sync must land before paint, or a row whose selected tab sits
 * off-screen shows tab one and then jumps. `useLayoutEffect` warns when React
 * renders on the server, which every Next.js app here does even for a
 * 'use client' module, so the server branch takes the effect that no-ops. */
const useSyncBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export { overflowEdges, pageTravel, tabOverflowLabels } from 'BomdisoftComponents/tab/delegate';
export type { TabOverflowLabels } from 'BomdisoftComponents/tab/render';

/**
 * The scrolling half of a tab row, shared by every tab surface on the platform.
 *
 * A hook rather than a wrapper because the three rows that need it are not one
 * component and should not become one: `UnderlineTabs` is a controlled row,
 * `HorizontalTab` owns a panel as well, and the docs nav's segments drive the
 * router. Each keeps its own markup, roles and approved styling and spreads
 * this onto the row it already renders, which is the same shape `useReorder`
 * takes for the same reason.
 *
 * What the caller must place: `frameProps` on a positioned wrapper, `rowProps`
 * on the scrolling row, and `<TabOverflowArrows>` inside the frame.
 *
 * `selected` is whatever the caller already uses to mark the active tab, and it
 * is here so the hook can own the reveal rather than leaving three rows to wire
 * the same effect three ways. Its VALUE is never read: it is the change signal,
 * and the element is found from the DOM, so a caller may pass an index, a route
 * id or a union member without the hook knowing which.
 */
/** How the hook finds the element it must keep on screen. A tab row marks its
 *  own with `aria-selected`; a segmented control presses its segments instead,
 *  and would otherwise reveal nothing because the tab selector matches none of
 *  its buttons. */
const SELECTED_TAB = '[role="tab"][aria-selected="true"]';

export const useTabOverflow = ({ frameClassName, selected, selectedSelector = SELECTED_TAB }: { frameClassName?: string; selected?: string | number | null; selectedSelector?: string } = {}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const settled = useRef(false);
  const [edges, setEdges] = useState({ end: false, start: false });

  const sync = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    setEdges(overflowEdges(row.scrollLeft, row.scrollWidth, row.clientWidth));
  }, []);

  /* ResizeObserver on the row alone misses the case that actually breaks it:
   * the row's own box is unchanged while its CONTENT rewraps or a label swaps
   * to a longer translation, so the scroll width moves and the edges do not.
   * Observing the first child covers content growth, and a MutationObserver
   * covers tabs being added or removed. */
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const observer = new ResizeObserver(sync);
    observer.observe(row);
    for (const child of row.children) observer.observe(child);
    const mutations = new MutationObserver(sync);
    mutations.observe(row, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [sync]);

  /** Brings the selected tab into the visible strip. Instant on the first run,
   *  because a row animating itself on page load reads as a glitch, and smooth
   *  afterwards so a deliberate change is followed. */
  const revealSelected = useCallback(() => {
    const row = rowRef.current;
    const active = row?.querySelector<HTMLElement>(selectedSelector);
    if (!active) return;
    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    active.scrollIntoView({ behavior: settled.current && !reduced ? 'smooth' : 'auto', block: 'nearest', inline: 'nearest' });
    settled.current = true;
    sync();
  }, [selectedSelector, sync]);

  useSyncBeforePaint(() => {
    /* Reading `selected` is what makes it a real dependency rather than one the
     * linter strips: the effect exists to re-run when the selection moves, and
     * the reveal reads the DOM rather than this value. */
    if (selected === undefined) sync();
    else revealSelected();
  }, [revealSelected, selected, sync]);

  const page = useCallback((direction: 1 | -1) => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollBy({ behavior: 'smooth', left: pageTravel(row.clientWidth, direction, getComputedStyle(row).direction === 'rtl') });
  }, []);

  return {
    edges,
    /* Outer spacing belongs on the FRAME, never on the row. The chevron slots
     * are `height: 100%` of the frame, so a margin left on the row makes the
     * frame taller than the tabs and the chevrons centre below them, sitting on
     * the row's bottom border. Measured in the docs rail: a 10px row margin put
     * them 5px low. */
    frameProps: { className: joinClasses('bomdisoft-tab-overflow', frameClassName), 'data-more-end': edges.end ? 'true' : undefined, 'data-more-start': edges.start ? 'true' : undefined },
    page,
    revealSelected,
    rowProps: { onScroll: sync, ref: rowRef },
    /** Compose onto the row's own class; it carries the scroll box and the fade. */
    scrollerClassName: 'bomdisoft-tab-overflow-scroller',
  };
};

export type TabOverflowArrowsProps = {
  labels?: TabOverflowLabels;
  onPage: (direction: 1 | -1) => void;
};

const Chevron = ({ back }: { back: boolean }): ReactNode => (
  <svg aria-hidden='true' fill='none' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2.4' viewBox='0 0 24 24'>
    <path d={back ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
  </svg>
);

/**
 * The two chevrons. Rendered unconditionally and revealed by the frame's
 * data attributes rather than mounted and unmounted, so pressing one at the
 * moment its edge retires cannot remove the button from under the pointer.
 *
 * Carries a tooltip as well as an aria-label, per the icon-only-button rule.
 * Out of the tab order deliberately: the row is already keyboard-operable with
 * the arrow keys and reveals its selection, so a tab stop per chevron would add
 * two stops to every tab row on the platform to reach a duplicate of behaviour
 * the keyboard already has.
 */
export const TabOverflowArrows = ({ labels, onPage }: TabOverflowArrowsProps): ReactNode => {
  const lang = useParams()?.lang;
  const named = labels ?? tabOverflowLabels(getLanguageFromLocale(typeof lang === 'string' ? lang : undefined));

  return (
    <>
      <span className='bomdisoft-tab-overflow-slot bomdisoft-tab-overflow-start'>
        <Tooltip placement='bottom' title={named.back}>
          <button aria-label={named.back} className='bomdisoft-tab-overflow-nudge' data-testid='tab-scroll-back' onClick={() => onPage(-1)} tabIndex={-1} type='button'>
            <Chevron back />
          </button>
        </Tooltip>
      </span>
      <span className='bomdisoft-tab-overflow-slot bomdisoft-tab-overflow-end'>
        <Tooltip placement='bottom' title={named.forward}>
          <button aria-label={named.forward} className='bomdisoft-tab-overflow-nudge' data-testid='tab-scroll-forward' onClick={() => onPage(1)} tabIndex={-1} type='button'>
            <Chevron back={false} />
          </button>
        </Tooltip>
      </span>
    </>
  );
};
